import uiI18n from "../i18n";
import { apiRequest, ApiError, assertSession, getCurrentUser, jsonOptions } from "./apiClient";
import { listingById } from "./listingService";

export const reservationStatus = (status) => ({ EnAttente: 0, Acceptee: 1, Rejetee: 2 }[status] ?? Number(status));
export const canAcceptReservation = (reservation) => reservationStatus(reservation.status) === 0 &&
  Boolean(reservation.uidUser) && reservation.contactName !== "Compte supprimé" && !reservation.propertyIsDeleted;
export const normalizeReservation = (reservation) => ({ ...reservation, status: reservationStatus(reservation.status) });

export function calendarDate(value) {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) throw new Error(uiI18n.t("ui.invalid_date"));
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  }
  return String(value || "").slice(0, 10);
}

export function stayNights(arrival, depart) {
  const day = (value) => {
    const text = calendarDate(value);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return NaN;
    const date = new Date(`${text}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === text ? date.getTime() : NaN;
  };
  return (day(depart) - day(arrival)) / 86400000;
}

// Display estimate only; the API recalculates the final amount.
export function reservationEstimate(property, days) {
  const price = Number(property?.offre && Number(property.prixReduit) > 0 && Number(property.prixReduit) < Number(property.prix)
    ? property.prixReduit : property?.prix) || 0;
  const round = value => Math.round((value + Number.EPSILON) * 100) / 100;
  const nights = Number.isFinite(days) && days > 0 ? days : 0;
  const offeredRate = Number(nights >= 30 ? property?.offreMonth : nights >= 7 ? property?.offreWeek : 0);
  const discountRate = Number.isFinite(offeredRate) ? Math.min(100, Math.max(0, offeredRate)) : 0;
  const subtotal = round(price * nights);
  const discountAmount = round(subtotal * discountRate / 100);
  return { subtotal, total: round(subtotal - discountAmount), discountRate, discountAmount };
}

const pending = new Set();
export async function addReservation(input) {
  const user = await getCurrentUser();
  const data = Object.fromEntries(Object.entries(input).map(([key, value]) => [key.toLowerCase(), value]));
  const arrival = calendarDate(data.arrival), depart = calendarDate(data.depart);
  const days = stayNights(arrival, depart), guests = Number(data.nbtraveler);
  if (!Number.isInteger(days) || days <= 0 || arrival < calendarDate(new Date())) throw new ApiError(400, uiI18n.t("ui.choose_a_check_in_date_from_today_and_a_check_out_date_after_check_in"));
  if (!Number.isInteger(guests) || guests < 1) throw new ApiError(400, uiI18n.t("ui.invalid_number_of_guests"));
  const key = `${user.uid}:${data.proporietsid}:${arrival}:${depart}`;
  if (pending.has(key)) throw new ApiError(409, uiI18n.t("ui.a_request_is_already_in_progress"));
  pending.add(key);
  try {
    const property = await listingById("Vacation", data.proporietsid);
    if (!property.uid || property.uid === user.uid) throw new ApiError(400, uiI18n.t("ui.you_cannot_book_your_own_accommodation"));
    if (!(property.maxGuests > 0) || guests > property.maxGuests) throw new ApiError(400, uiI18n.t("ui.the_number_of_guests_exceeds_the_accommodation_s_capacity"));
    const dates = await getReservedDates(data.proporietsid);
    if (dates.some(({ startDate, endDate }) => arrival < calendarDate(endDate) && depart > calendarDate(startDate))) {
      throw new ApiError(409, uiI18n.t("ui.these_dates_are_no_longer_available"));
    }
    assertSession(user.uid);
    // Price, total, discount, owner and status are calculated by the server.
    return await apiRequest("Reservation/AddReservation", jsonOptions("POST", {
      proporietsID: data.proporietsid, uidUser: user.uid, arrival, depart, nbTraveler: guests,
      contactName: data.contactname || user.displayName || data.name || "",
      email: data.email || user.email || "", phone: data.phone || "", message: data.message || "",
      ads: data.ads || "", dateReservation: calendarDate(new Date()),
    }));
  } finally { pending.delete(key); }
}

export const getReservedDates = (id) => apiRequest(`Reservation/GetReservationDates/${encodeURIComponent(id)}`, { authenticated: false });
async function mine(route, id) {
  const user = await getCurrentUser();
  const result = await apiRequest(`Reservation/${route}/${id ? `${encodeURIComponent(id)}/` : ""}${encodeURIComponent(user.uid)}`);
  return Array.isArray(result) ? result.map(normalizeReservation) : (result && typeof result === "object" ? normalizeReservation(result) : result);
}
export const getMyReservation = (id) => mine("GetmyReservation", id);
export const getAllReservation = () => mine("GetAllReservation");
export const getAllRequest = () => mine("GetAllRequest");
export const getCountReservationOnHold = () => mine("GetReservationsOnHold");

export async function deleteReservation(reservation) {
  const user = await getCurrentUser();
  if (reservation.uidUser !== user.uid) throw new ApiError(403, uiI18n.t("ui.this_booking_does_not_belong_to_you"));
  if (reservationStatus(reservation.status) !== 0) throw new ApiError(409, uiI18n.t("ui.only_a_pending_request_can_be_cancelled"));
  return apiRequest("Reservation/Delete", jsonOptions("DELETE", { id: reservation.id }));
}

export async function updateReservationStatus(reservation, status) {
  const user = await getCurrentUser();
  if (reservation.uidLandlord !== user.uid) throw new ApiError(403, uiI18n.t("ui.you_do_not_own_this_booking"));
  if (reservationStatus(reservation.status) !== 0 || ![1, 2].includes(status)) throw new ApiError(409, uiI18n.t("ui.this_request_has_already_been_processed"));
  if (status === 1 && !canAcceptReservation(reservation)) throw new ApiError(409, uiI18n.t("ui.this_request_can_no_longer_be_accepted_the_account_or_listing_was_deleted"));
  return status === 1
    ? apiRequest("Reservation", jsonOptions("PUT", { id: reservation.id }))
    : apiRequest("Email/AcceptOrRejectVacation", jsonOptions("POST", { ...reservation, status: 2 }));
}
export const acceptReservation = (reservation) => updateReservationStatus(reservation, 1);
